import React from "react";
import { Box, Button, Typography } from "@mui/material";
import { DialogFrame } from "../../Policy/BriskCertificateList/BriskV2Dialogs";
import { rupeesText, transactionTypeOf, whenText } from "./roadzenWalletUtils";

const buttonSx = { textTransform: "none", borderRadius: "6px" };
const sectionSx = { fontSize: 13, fontWeight: 700, color: "#1E3A5F", mt: 2, mb: 1 };

/** Label / value pairs in two columns (one on a phone); empty values are left out. */
const Pairs = ({ items }) => (
  <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, columnGap: 3, rowGap: 1.25 }}>
    {items
      .filter(([, v]) => v !== null && v !== undefined && v !== "")
      .map(([label, value]) => (
        <Box key={label} sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: 11.5, color: "#667085", fontWeight: 600 }}>{label}</Typography>
          <Typography sx={{ fontSize: 13.5, color: "#101828", overflowWrap: "anywhere" }}>{value}</Typography>
        </Box>
      ))}
  </Box>
);

/**
 * One statement row in full (owner: "click the row then show the full details if it exists"): what RoadZen wrote, and
 * when the row was made from this app, who made it and how (from our own records). A row made elsewhere says so.
 */
const StatementRowDialog = ({ row, onClose }) => {
  if (!row) return null;
  const d = row.details;
  return (
    <DialogFrame
      title="Transaction details"
      subtitle={`${transactionTypeOf(row)} · ${row.type === "DEBIT" ? "−" : "+"}${rupeesText(row.amountPaise)}`}
      onClose={onClose}
      maxWidth={620}
      footer={
        <Button variant="outlined" sx={buttonSx} onClick={onClose}>
          Close
        </Button>
      }
    >
      <Typography sx={{ ...sectionSx, mt: 0 }}>In RoadZen's statement</Typography>
      <Pairs
        items={[
          ["Date", whenText(row.createdAt)],
          ["Type", `${transactionTypeOf(row)} (${row.type === "DEBIT" ? "Debit" : "Credit"})`],
          ["Amount", rupeesText(row.amountPaise)],
          ["Balance after", rupeesText(row.balanceAfterPaise)],
          ["Certificate No", row.certificateNumber || ""],
          ["Note", row.note || ""],
          ["RoadZen entry", row.id || ""],
        ]}
      />
      <Typography sx={sectionSx}>From this app</Typography>
      {d ? (
        <Pairs
          items={[
            ["Done by", d.byName || "Not known"],
            ["When", d.at ? whenText(d.at) : ""],
            ["How", d.kind],
            ["Amount asked", typeof d.amountPaise === "number" ? rupeesText(d.amountPaise) : ""],
            ["Balance before", typeof d.balanceBeforePaise === "number" ? rupeesText(d.balanceBeforePaise) : ""],
            ["Balance after", typeof d.balanceAfterPaise === "number" ? rupeesText(d.balanceAfterPaise) : ""],
            ["Result", d.result || ""],
            ["Certificate", d.certificateNumber || ""],
            ["Customer", d.customerName || ""],
            ["Product", d.product || ""],
            ["Payment link", d.topupId || ""],
            ["Link note", d.note || ""],
            ["Paid at", d.paidAt ? whenText(d.paidAt) : ""],
            ["Request", d.requestId || ""],
          ]}
        />
      ) : (
        <Typography sx={{ fontSize: 13, color: "#475467" }} data-testid="roadzen-row-outside">
          This entry was not made from this app (for example from RoadZen's own portal or Postman), so there is no record here of who made it.
        </Typography>
      )}
      {d && d.message && (
        <Typography sx={{ fontSize: 12.5, color: "#475467", mt: 1.5, overflowWrap: "anywhere" }}>{d.message}</Typography>
      )}
    </DialogFrame>
  );
};

export default StatementRowDialog;
